import { useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { AiOutlineClose, AiOutlineCheck } from "react-icons/ai";
import { FaLocationCrosshairs } from "react-icons/fa6";
import {
    MEDIA_BTN_DELETE_MD,
    MEDIA_BTN_DELETE_SM,
} from "../shared/mediaIconButtonClasses";
import MediaEditGroup from "../shared/MediaEditGroup";
import ConfirmPopUp from "../../popup/confirmPopUp";
import {
    getGameFieldBorderClass,
    quizChoiceFieldTarget,
    quizQuestionFieldTarget,
} from "../shared/fieldErrorHighlight";
import { useLocale, useTranslations } from "next-intl";
import { getChoiceLabels } from "../shared/choiceLabels";

type Position = {
    x: number;
    y: number;
};

type Answer = {
    id: number;
    text: string;
    isCorrect: boolean;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
};

type Question = {
    id: number;
    text: string;
    shuffle_answer: boolean;
    position: Position | null;
    skill_listening_speaking: boolean;
    skill_lang_nature: boolean;
    skill_reading: boolean;
    skill_writing: boolean;
    hasHint: boolean;
    hintText: string;
    hintImageFileName: string;
    hintImageFile: File | null;
    hintImageUrl: string | null;
    hintAudioFileName: string;
    hintAudioFile: File | null;
    hintAudioUrl: string | null;
    imageFileName: string;
    imageFile: File | null;
    imageUrl: string | null;
    audioFileName: string;
    audioFile: File | null;
    audioUrl: string | null;
    answers: Answer[];
};

const DEFAULT_POSITION: Position = {
    x: 0.5,
    y: 0.5,
};

function formatPositionValue(value: number) {
    return value.toFixed(3).replace(/\.?0+$/, "");
}

function getEffectivePosition(position: Position | null) {
    return position ?? DEFAULT_POSITION;
}

type QuestionBlockProps = {
    index: number;
    question: Question;
    onQuestionTextChange: (questionId: number, value: string) => void;
    onQuestionHintTextChange: (questionId: number, value: string) => void;
    onQuestionHintImageFileChange: (questionId: number, file: File) => void;
    onQuestionHintAudioFileChange: (questionId: number, file: File) => void;
    onEnableQuestionHint: (questionId: number) => void;
    onClearQuestionHint: (questionId: number) => void;
    onQuestionImageFileChange: (questionId: number, file: File) => void;
    onQuestionAudioFileChange: (questionId: number, file: File) => void;
    onQuestionImageDelete?: (questionId: number) => void;
    onQuestionAudioDelete?: (questionId: number) => void;
    onQuestionHintImageDelete?: (questionId: number) => void;
    onQuestionHintAudioDelete?: (questionId: number) => void;
    onQuestionShuffleAnswerChange: (questionId: number, checked: boolean) => void;
    onAnswerTextChange: (questionId: number, answerId: number, value: string) => void;
    onAnswerImageFileChange: (questionId: number, answerId: number, file: File) => void;
    onAnswerAudioFileChange: (questionId: number, answerId: number, file: File) => void;
    onAnswerImageDelete?: (questionId: number, answerId: number) => void;
    onAnswerAudioDelete?: (questionId: number, answerId: number) => void;
    onToggleAnswerCorrect: (questionId: number, answerId: number) => void;
    onAddAnswer: (questionId: number) => void;
    onRemoveAnswer: (questionId: number, answerId: number) => void | Promise<void>;
    onRemoveQuestion: (questionId: number) => void | Promise<void>;
    onMoveQuestionUp: (questionId: number) => void;
    onMoveQuestionDown: (questionId: number) => void;
    onQuestionSkillChange: (
        questionId: number,
        skillKey:
            | "skill_listening_speaking"
            | "skill_lang_nature"
            | "skill_reading"
            | "skill_writing",
        checked: boolean
    ) => void;
    onSelectQuestion: (questionId: number) => void;
    isSelected: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    canRemoveQuestion: boolean;
    fieldErrorHighlightKey?: string | null;
};

export default function QuestionBlock({
    index,
    question,
    onQuestionTextChange,
    onQuestionHintTextChange,
    onQuestionHintImageFileChange,
    onQuestionHintAudioFileChange,
    onEnableQuestionHint,
    onClearQuestionHint,
    onQuestionImageFileChange,
    onQuestionAudioFileChange,
    onQuestionImageDelete,
    onQuestionAudioDelete,
    onQuestionHintImageDelete,
    onQuestionHintAudioDelete,
    onQuestionShuffleAnswerChange,
    onAnswerTextChange,
    onAnswerImageFileChange,
    onAnswerAudioFileChange,
    onAnswerImageDelete,
    onAnswerAudioDelete,
    onToggleAnswerCorrect,
    onAddAnswer,
    onRemoveAnswer,
    onRemoveQuestion,
    onMoveQuestionUp,
    onMoveQuestionDown,
    onQuestionSkillChange,
    onSelectQuestion,
    isSelected,
    canMoveUp,
    canMoveDown,
    canRemoveQuestion,
    fieldErrorHighlightKey = null,
}: QuestionBlockProps) {
    const locale = useLocale();
    const choiceLabels = getChoiceLabels(locale);
    const tSituation = useTranslations("situation");
    const questionFieldTarget = quizQuestionFieldTarget(question.id);
    const answersFieldTarget = quizQuestionFieldTarget(question.id, "answers");
    const isQuestionFieldError = questionFieldTarget.highlightKey === fieldErrorHighlightKey;
    const isAnswersFieldError = answersFieldTarget.highlightKey === fieldErrorHighlightKey;
    const canAddMoreAnswer = question.answers.length < choiceLabels.length;
    const questionImageInputId = `question-image-${question.id}`;
    const questionAudioInputId = `question-audio-${question.id}`;
    const questionHintImageInputId = `question-hint-image-${question.id}`;
    const questionHintAudioInputId = `question-hint-audio-${question.id}`;
    const [pendingDelete, setPendingDelete] = useState<null | { type: "question" } | { type: "answer"; answerId: number }>(null);

    return (
        <div className="mb-4 flex w-full min-w-0 items-start gap-2" onClick={() => onSelectQuestion(question.id)}>
            <div className="w-10 shrink-0 pt-5 text-center">
                <span className="text-lg font-semibold text-[#5a5a5a]">{index}.</span>
            </div>
            <div className="min-w-0 flex-1 rounded-lg border border-[#e5e5e5] bg-[#fafafa] p-4 transition-colors shadow-md sm:p-5">

                <div className="mb-2 flex flex-col gap-2">
                    <label className="text-sm font-bold text-[#555]">{tSituation("question")}</label>
                    <div className="relative">
                        <div
                            id={questionFieldTarget.scrollId}
                            className={`flex h-9.5 items-center rounded-md border bg-white px-3 focus-within:border-blue-400 ${getGameFieldBorderClass(isQuestionFieldError)}`}
                        >
                            <input
                                value={question.text}
                                onChange={(event) => onQuestionTextChange(question.id, event.target.value)}
                                className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                            />
                            <div className="flex items-center gap-1 pl-2">
                                <MediaEditGroup
                                    imageInputId={questionImageInputId}
                                    imageFile={question.imageFile}
                                    imageUrl={question.imageUrl}
                                    imageAlt={tSituation("questionImage")}
                                    onImageChange={(file) => onQuestionImageFileChange(question.id, file)}
                                    onImageDelete={() => onQuestionImageDelete?.(question.id)}
                                    audioInputId={questionAudioInputId}
                                    audioFile={question.audioFile}
                                    audioUrl={question.audioUrl}
                                    onAudioChange={(file) => onQuestionAudioFileChange(question.id, file)}
                                    onAudioDelete={() => onQuestionAudioDelete?.(question.id)}
                                />
                            </div>
                        </div>
                    </div>

                    {question.hasHint ? (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-bold text-[#555]">{tSituation("hint")}</label>
                            <div className="relative">
                                <div className="flex h-9.5 items-center rounded-md border border-[#a8a8a8] bg-white px-3 focus-within:border-blue-400">
                                    <input
                                        value={question.hintText}
                                        onChange={(event) => onQuestionHintTextChange(question.id, event.target.value)}
                                        className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                                        placeholder={tSituation("typeHint")}
                                    />
                                    <div className="flex items-center gap-1 pl-2">
                                        <MediaEditGroup
                                            imageInputId={questionHintImageInputId}
                                            imageFile={question.hintImageFile}
                                            imageUrl={question.hintImageUrl}
                                            imageAlt={tSituation("hintImage")}
                                            onImageChange={(file) => onQuestionHintImageFileChange(question.id, file)}
                                            onImageDelete={() => onQuestionHintImageDelete?.(question.id)}
                                            audioInputId={questionHintAudioInputId}
                                            audioFile={question.hintAudioFile}
                                            audioUrl={question.hintAudioUrl}
                                            onAudioChange={(file) => onQuestionHintAudioFileChange(question.id, file)}
                                            onAudioDelete={() => onQuestionHintAudioDelete?.(question.id)}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => onClearQuestionHint(question.id)}
                                            className={MEDIA_BTN_DELETE_MD}
                                            title={tSituation("clearHint")}
                                        >
                                            <MdDeleteOutline size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onEnableQuestionHint(question.id)}
                            className="cursor-pointer self-start text-xs font-medium text-[#4ca2e2] hover:text-blue-600 hover:underline"
                        >
                            {tSituation("addHint")}
                        </button>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <label className="text-sm font-bold text-[#555]">{tSituation("answer")}</label>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#555] cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={question.shuffle_answer}
                                onChange={(event) => onQuestionShuffleAnswerChange(question.id, event.target.checked)}
                                className="h-4 w-4 rounded border-[#ccc]"
                            />
                            <span>{tSituation("swapAnswer")}</span>
                        </label>
                    </div>
                    {question.answers.length > 0 ? (
                        <div
                            id={answersFieldTarget.scrollId}
                            className={`grid grid-cols-1 gap-2 rounded-md border p-1 sm:grid-cols-2 ${getGameFieldBorderClass(isAnswersFieldError, "border-transparent")}`}
                        >
                            {question.answers.map((answer, answerIndex) => {
                                const answerImageInputId = `answer-image-${question.id}-${answer.id}`;
                                const answerAudioInputId = `answer-audio-${question.id}-${answer.id}`;
                                const choiceFieldTarget = quizChoiceFieldTarget(question.id, answer.id);
                                const isChoiceFieldError = choiceFieldTarget.highlightKey === fieldErrorHighlightKey;

                                return (
                                    <div
                                        key={answer.id}
                                        id={choiceFieldTarget.scrollId}
                                        className={`flex h-9.5 items-center gap-2 rounded-md border bg-white px-3 ${getGameFieldBorderClass(isChoiceFieldError)}`}
                                    >
                                        <span className="inline-flex min-h-7 min-w-7 shrink-0 items-center justify-center rounded-md border border-[#cfe8f7] bg-[#eef6fc] px-2 text-sm font-bold text-[#555] shadow-sm">
                                            {choiceLabels[answerIndex] ?? `${answerIndex + 1}.`}
                                        </span>

                                        <button
                                            type="button"
                                            onClick={() => onToggleAnswerCorrect(question.id, answer.id)}
                                            title={answer.isCorrect ? tSituation("correctAnswerToggle") : tSituation("incorrectAnswerToggle")}
                                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-sm font-bold shadow-sm transition-colors cursor-pointer ${answer.isCorrect
                                                    ? "border-[#15B427]/45 bg-[#e8f8ea] text-[#15B427] hover:bg-[#dcf5df]"
                                                    : "border-[#ffb3b3] bg-[#fff5f5] text-[#e02020] hover:bg-[#ffecec]"
                                                }`}
                                        >
                                            {answer.isCorrect ? "✓" : "✕"}
                                        </button>

                                        <input
                                            value={answer.text}
                                            onChange={(event) => onAnswerTextChange(question.id, answer.id, event.target.value)}
                                            className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
                                        />

                                        <div className="flex shrink-0 items-center gap-1 pl-2">
                                            <MediaEditGroup
                                                imageInputId={answerImageInputId}
                                                imageFile={answer.imageFile}
                                                imageUrl={answer.imageUrl}
                                                imageAlt={`${tSituation("optionImage")} ${choiceLabels[answerIndex] ?? answerIndex + 1}`}
                                                onImageChange={(file) => onAnswerImageFileChange(question.id, answer.id, file)}
                                                onImageDelete={() => onAnswerImageDelete?.(question.id, answer.id)}
                                                audioInputId={answerAudioInputId}
                                                audioFile={answer.audioFile}
                                                audioUrl={answer.audioUrl}
                                                onAudioChange={(file) => onAnswerAudioFileChange(question.id, answer.id, file)}
                                                onAudioDelete={() => onAnswerAudioDelete?.(question.id, answer.id)}
                                                size="sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPendingDelete({ type: "answer", answerId: answer.id })}
                                                className={`${MEDIA_BTN_DELETE_SM} ml-0.5`}
                                                title={tSituation("delete")}
                                            >
                                                <MdDeleteOutline size={16} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}

                    <div className="mt-1 flex justify-center">
                        <button
                            type="button"
                            onClick={() => onAddAnswer(question.id)}
                            disabled={!canAddMoreAnswer}
                            className="cursor-pointer rounded-md bg-[#ffeb3b] px-4 py-1.5 text-sm font-semibold text-[#333] hover:bg-[#f4d735] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {tSituation("addAnswer")}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3">
                    <button
                        type="button"
                        onClick={() => setPendingDelete({ type: "question" })}
                        disabled={!canRemoveQuestion}
                        className={`${MEDIA_BTN_DELETE_MD} disabled:cursor-not-allowed disabled:opacity-50`}
                        title={canRemoveQuestion ? tSituation("deleteQuestionLabel") : tSituation("minQuestionsRequired")}
                    >
                        <MdDeleteOutline size={18} />
                    </button>
                </div>
            </div>

            <div className="flex w-10 shrink-0 flex-col items-center gap-1 pt-5 text-[#555]">
                <button
                    type="button"
                    onClick={() => onMoveQuestionUp(question.id)}
                    disabled={!canMoveUp}
                    className="cursor-pointer text-xl hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                    title={tSituation("moveUp")}
                >
                    ↑
                </button>
                <button
                    type="button"
                    onClick={() => onMoveQuestionDown(question.id)}
                    disabled={!canMoveDown}
                    className="cursor-pointer text-xl hover:text-black disabled:cursor-not-allowed disabled:opacity-30"
                    title={tSituation("moveDown")}
                >
                    ↓
                </button>
            </div>
            {pendingDelete ? (
                <ConfirmPopUp
                    title={tSituation("confirmDelete")}
                    message={pendingDelete.type === "question" ? tSituation("confirmDeleteQuestion") : tSituation("confirmDeleteOption")}
                    cancelText={tSituation("cancel")}
                    confirmText={tSituation("confirm")}
                    onCancel={() => setPendingDelete(null)}
                    onConfirm={async () => {
                        if (pendingDelete.type === "question") {
                            await onRemoveQuestion(question.id);
                        } else {
                            await onRemoveAnswer(question.id, pendingDelete.answerId);
                        }
                        setPendingDelete(null);
                    }}
                />
            ) : null}
        </div>
    );
}
